import { esc } from '../shared/models/html-escape';
import { categoryLabel } from '../shared/models/ais-vessel-types';

/**
 * AISSTREAM'S POPUP MARKUP — the vessel card and the cluster list (D14).
 *
 * Pure string builders — no Leaflet, no Angular, no `this` — so the escaping
 * and the "—" placeholders can be tested without a map. The controller keeps
 * everything Leaflet: binding, positioning, `openOn`.
 *
 * The mirror of Voyage Radar's `vessel-popup.ts`, and provider-side for the
 * same reason (D11): the fields it names — `staticData.name`, `navStatusLabel`,
 * `shipTypeCategory` — are this feed's dialect, and the card it draws is this
 * product's. The classes (`ais-vessel-popup`, `ais-popup__*`) are shared
 * styling in `styles.scss`, because Leaflet mounts popups outside the
 * component's DOM where scoped styles never reach.
 *
 * `esc` is imported, not redefined. There is one escaper on this map (D11) and
 * this file is why: it had a private copy for a day.
 */

/** Format an ISO timestamp as e.g. "Tue, 21 Jul 2026 19:47:18 UTC". */
export function formatAisstreamUtc(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const days   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const p = (n: number) => String(n).padStart(2, '0');
  return `${days[d.getUTCDay()]}, ${p(d.getUTCDate())} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()} ` +
    `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`;
}

/** Dark-themed vessel popup: name, MMSI, Type, Status, SOG, COG, Time UTC. */
export function buildAisstreamVesselPopup(
  vessel: any,
  category: string,
  color: string,
): string {
  const pos  = vessel?.lastPositionUpdate ?? {};
  const stat = vessel?.staticData ?? {};

  const name   = esc(stat.name ?? 'Unknown');
  const mmsi   = esc(stat.mmsi ?? '—');
  const type   = esc(categoryLabel(category));
  const status = esc(pos.navStatusLabel ?? '—');
  const sog    = pos.speed != null ? `${esc(pos.speed)} kn` : '—';
  const cog    = pos.course != null ? `${esc(Math.round(Number(pos.course)))}°` : '—';
  const time   = pos.timestamp ? esc(formatAisstreamUtc(pos.timestamp)) : '—';

  return (
    `<div class="ais-popup">` +
      `<div class="ais-popup__head"><span class="ais-popup__dot" style="background:${color}"></span>` +
        `<span class="ais-popup__name">${name}</span></div>` +
      `<div class="ais-popup__row"><span class="ais-popup__k">MMSI:</span><span class="ais-popup__mmsi">${mmsi}</span></div>` +
      `<div class="ais-popup__row"><span class="ais-popup__k">Type:</span><span class="ais-popup__v">${type}</span></div>` +
      `<div class="ais-popup__row"><span class="ais-popup__k">Status:</span><span class="ais-popup__v">${status}</span></div>` +
      `<div class="ais-popup__row"><span class="ais-popup__k">SOG:</span><span class="ais-popup__v">${sog}</span></div>` +
      `<div class="ais-popup__row"><span class="ais-popup__k">COG:</span><span class="ais-popup__v">${cog}</span></div>` +
      `<div class="ais-popup__row"><span class="ais-popup__k">Time:</span><span class="ais-popup__time">${time}</span></div>` +
    `</div>`
  );
}

/** How many hulls a cluster popup names before it stops and counts the rest. */
export const CLUSTER_LIST_LIMIT = 12;

/**
 * The list shown when a count bubble is clicked at a zoom that cannot separate
 * its members — hulls moored together, or a map already at max zoom.
 *
 * Capped with a remainder line: this answers "what is under here", not "list
 * everything", and eighty rows would run off the map.
 *
 * Takes plain `{ name, mmsi }` rows rather than the Leaflet markers the caller
 * holds, which is what keeps this file free of Leaflet — the same shape Voyage
 * Radar's `buildClusterPopup` takes, so the two cards stay comparable.
 */
export function buildAisstreamClusterPopup(
  vessels: Array<{ name?: any; mmsi?: any }>,
): string {
  const rows = vessels.slice(0, CLUSTER_LIST_LIMIT).map((v) =>
    `<div class="ais-popup__row">` +
      `<span class="ais-popup__v">${esc(v.name ?? 'Unknown')}</span>` +
      `<span class="ais-popup__mmsi">${esc(v.mmsi ?? '—')}</span></div>`,
  ).join('');
  const more = vessels.length > CLUSTER_LIST_LIMIT
    ? `<div class="ais-popup__row"><span class="ais-popup__k">` +
      `+${vessels.length - CLUSTER_LIST_LIMIT} more</span></div>`
    : '';
  return (
    `<div class="ais-popup">` +
      `<div class="ais-popup__head">` +
        `<span class="ais-popup__name">${vessels.length} vessels here</span></div>` +
      `${rows}${more}</div>`
  );
}
